import { useSetAtom } from "jotai";
import { useCallback } from "react";

import { ProceedToVerifyCodeStepAtom } from "../atoms/actions";
import { UserContactStatus } from "./__generated__/useCheckUserEmailStatusNotificationsMutation.graphql";
import { useCheckUserEmailStatusNotifications } from "./useCheckUserEmailStatusNotifications";
import { useCheckUserPhoneStatusNotifications } from "./useCheckUserPhoneStatusNotifications";
import { useCreateEmailVerificationNotifications } from "./useCreateEmailVerificationNotifications";
import { useCreatePhoneVerificationNotifications } from "./useCreatePhoneVerificationNotifications";

export const useCheckEmailorPhoneNotifications = () => {
  const goToCodeNotifications = useSetAtom(ProceedToVerifyCodeStepAtom);
  const [checkEmail, isCheckingEmail] = useCheckUserEmailStatusNotifications();
  const [checkPhone, isCheckingPhone] = useCheckUserPhoneStatusNotifications();
  const [createPhoneNotifications, isCreatingPhoneNotifications] =
    useCreatePhoneVerificationNotifications();
  const [createEmailNotifications, isCreatingEmailNotifications] =
    useCreateEmailVerificationNotifications();

  const isChecking =
    isCheckingEmail ||
    isCheckingPhone ||
    isCreatingPhoneNotifications ||
    isCreatingEmailNotifications;

  const handleSuccessResponse = useCallback(
    (value: string, response: UserContactStatus, type: "Email" | "Phone") => {
      const email = type === "Email" ? value : null;
      const phone = type === "Phone" ? value : null;

      const canProceedToNextStep =
        response === "NOT_TAKEN" ||
        response === "UNVERIFIED" ||
        response === "VERIFIED";

      if (canProceedToNextStep) {
        if (!!email) {
          createEmailNotifications(email, () => {
            goToCodeNotifications({ email, phone });
          });
        } else if (!!phone) {
          createPhoneNotifications(phone, () => {
            goToCodeNotifications({ email, phone });
          });
        }
      }
    },
    [createEmailNotifications, createPhoneNotifications, goToCodeNotifications]
  );

  const checkEmailOrPhoneNotifications = ({
    value,
    type,
  }: {
    value: string;
    type: "Email" | "Phone";
  }) => {
    if (type === "Email") {
      checkEmail(value, handleSuccessResponse);
    } else {
      checkPhone(value, handleSuccessResponse);
    }
  };

  return [checkEmailOrPhoneNotifications, isChecking] as const;
};
